import { ChevronDown, MoreHorizontal } from 'lucide-react';
import * as React from 'react';

import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useMainContext } from '@/context/main-context';
import { calculateMultiCoursePaymentsByMonth } from '@/lib/payment-history';
import {
  formatNumber,
  formatPaymentMethod,
  translateMonthToUzbek,
} from '@/lib/utils';
import { format } from 'date-fns';
import { Badge } from '../ui/badge';

export default function StudentPaymentHistory({
  student,
  data,
  setId,
  idKey,
  loading,
  setOpenDelete,
  setOpenEdit,
  totalAmount,
  selectedMonth,
  children,
}) {
  const { courses, groups } = useMainContext();
  const { courseStatuses, totalCourses } = calculateMultiCoursePaymentsByMonth(
    student,
    courses,
    groups
  );

  const date = new Date(selectedMonth);
  const monthName = date.toLocaleString('en-US', { month: 'long' });

  const [sorting, setSorting] = React.useState([]);
  const [columnFilters, setColumnFilters] = React.useState([]);
  const [columnVisibility, setColumnVisibility] = React.useState({});
  const [rowSelection, setRowSelection] = React.useState({});

  const columns = [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => (
        <div className="whitespace-nowrap">{row.getValue('name')}</div>
      ),
    },
    {
      accessorKey: 'course',
      header: 'Course',
      cell: ({ row }) => {
        const courseId = groups.find(
          (g) => g?.id === row.getValue('course')
        )?.courseId;
        return (
          <div className="whitespace-nowrap">
            {courses.find((course) => course?.id === courseId)?.courseTitle ||
              'Course not found'}
          </div>
        );
      },
    },
    {
      accessorKey: 'timestamp',
      header: 'Timestamp',
      cell: ({ row }) => {
        function formatDate(timestamp) {
          const { seconds, nanoseconds } = timestamp;
          const date = new Date(seconds * 1000 + nanoseconds / 1000000);
          return format(date, 'dd.MM.yyyy');
        }
        return (
          <div className="whitespace-nowrap">
            {formatDate(row.getValue('timestamp'))}
          </div>
        );
      },
    },
    {
      accessorKey: 'amount',
      header: 'Amount',
      cell: ({ row }) => {
        return (
          <div className="whitespace-nowrap">
            {formatNumber(row.getValue('amount'))} so'm
          </div>
        );
      },
    },
    {
      accessorKey: 'method',
      header: 'Payment Method',
      cell: ({ row }) => {
        return (
          <div className="whitespace-nowrap">
            {formatPaymentMethod(row.getValue('method'))}
          </div>
        );
      },
    },
    {
      id: 'actions',
      enableHiding: false,
      cell: ({ row }) => {
        const payment = row.original;

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => {
                  setId(payment[idKey]);
                  setOpenEdit(true);
                }}
              >
                Tahrirlash
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => {
                  setId(payment[idKey]);
                  setOpenDelete(true);
                }}
              >
                O'chirish
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  });

  return (
    <div className="w-full">
      <div
        className={`grid gap-4 ${
          courseStatuses?.length === 1
            ? 'grid-cols-1 max-w-sm'
            : courseStatuses?.length === 2
            ? 'grid-cols-1 md:grid-cols-2'
            : courseStatuses?.length === 3
            ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
            : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
        }`}
      >
        {courseStatuses.map((c) => (
          <div
            key={c.courseId}
            className="group relative bg-muted p-4 rounded-2xl border border-border"
          >
            <div className="flex items-start justify-between mb-3">
              <h3 className="font-semibold text-base text-gray-900 dark:text-white leading-tight text-balance pr-2">
                {c.courseTitle}
              </h3>
              <Badge
                className={`px-2 py-1 text-xs font-medium shrink-0 ${
                  c.status === 'paid'
                    ? 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400'
                    : c.status === 'partial'
                    ? 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400'
                    : 'bg-rose-100 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-400'
                }`}
              >
                {c.status === 'paid'
                  ? "To'langan"
                  : c.status === 'partial'
                  ? 'Qisman'
                  : 'Qarzdor'}
              </Badge>
            </div>

            <div className="space-y-2 mb-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Narx</span>
                <span className="font-semibold text-gray-900 dark:text-white">
                  {formatNumber(c.coursePrice)}
                </span>
              </div>

              <div className="flex items-center justify-between text-sm">
                <span className="text-emerald-600 dark:text-emerald-400">
                  To'langan
                </span>
                <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                  {formatNumber(c.paidAmount)}
                </span>
              </div>

              {c.debt > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-rose-600 dark:text-rose-400">
                    Qolgan
                  </span>
                  <span className="font-semibold text-rose-600 dark:text-rose-400">
                    {formatNumber(c.debt)}
                  </span>
                </div>
              )}
            </div>

            <div className="space-y-1 hidden">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  To'lov
                </span>
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                  {Math.round((c.paidAmount / c.coursePrice) * 100)}%
                </span>
              </div>
              <div className="w-full bg-gray-100 dark:bg-gray-800 rounded-full h-2 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-300 ${
                    c.status === 'paid'
                      ? 'bg-emerald-500'
                      : c.status === 'partial'
                      ? 'bg-amber-500'
                      : 'bg-rose-500'
                  }`}
                  style={{ width: `${(c.paidAmount / c.coursePrice) * 100}%` }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
      <h4 className="text-lg my-2">{student?.fullName}ning to'lovlar tarixi</h4>
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-2 lg:mb-0">
        <div className="flex items-center py-2 gap-2">
          <Input
            placeholder="Ism bilan qidirish..."
            value={table.getColumn('name')?.getFilterValue() ?? ''}
            onChange={(event) =>
              table.getColumn('name')?.setFilterValue(event.target.value)
            }
            className="w-full md:w-64 xl:w-72"
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">
                Ustunlar <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {table
                .getAllColumns()
                .filter((column) => column.getCanHide())
                .map((column) => {
                  return (
                    <DropdownMenuCheckboxItem
                      key={column.id}
                      className="capitalize"
                      checked={column.getIsVisible()}
                      onCheckedChange={(value) =>
                        column.toggleVisibility(!!value)
                      }
                    >
                      {column.id}
                    </DropdownMenuCheckboxItem>
                  );
                })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <>{children}</>
      </div>
      <div className="rounded-b-md border max-w-[44rem] min-w-full overflow-x-auto">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id} className="whitespace-nowrap">
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns?.length}
                  className="h-16 text-center"
                >
                  {loading ? 'Loading...' : 'No results.'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-start justify-between space-x-2 py-4">
        {selectedMonth && (
          <small className="text-muted-foreground hidden lg:block">
            {translateMonthToUzbek(monthName)} oyi uchun umumiy summa:{' '}
            <b>{totalAmount}</b> uzs
          </small>
        )}
        <div className="space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            Orqaga
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            Oldinga
          </Button>
        </div>
      </div>
    </div>
  );
}
